import * as React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Secondary line, e.g. "3 urgent" or a delta. */
  hint?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  /** Makes the whole card a link (dashboard tiles). */
  to?: string;
  tone?: 'default' | 'primary' | 'success' | 'warning' | 'destructive';
  loading?: boolean;
}

const toneClass: Record<NonNullable<StatCardProps['tone']>, string> = {
  default: 'text-muted-foreground',
  primary: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

/** Dashboard KPI tile: label, big number, optional hint and icon. */
export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  to,
  tone = 'default',
  loading,
  className,
  ...props
}: StatCardProps) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {Icon ? <Icon className={cn('h-4 w-4', toneClass[tone])} aria-hidden="true" /> : null}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-20" />
      ) : (
        <p className="mt-1 text-3xl font-semibold tabular-nums tracking-tight">{value}</p>
      )}
      {hint ? <p className={cn('mt-1 text-xs', toneClass[tone])}>{hint}</p> : null}
    </>
  );
  const cardClass = cn(
    'block rounded-lg border bg-card p-5 text-card-foreground shadow-sm',
    to && 'outline-none transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring',
    className,
  );
  if (to) {
    return (
      <div {...props}>
        <Link to={to} className={cardClass}>
          {body}
        </Link>
      </div>
    );
  }
  return (
    <div className={cardClass} {...props}>
      {body}
    </div>
  );
}
